import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { 
  Search, 
  Brain, 
  Loader2, 
  Filter,
  X,
  Sparkles
} from "lucide-react";
import CandidateSearchResults, { SearchCandidate } from "./CandidateSearchResults";

interface CandidateSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobTitle: string;
  onAddCandidates: (candidateIds: string[]) => void;
}

interface Skill {
  id: string;
  name: string;
}

export default function CandidateSearchDialog({
  open,
  onOpenChange,
  jobId,
  jobTitle,
  onAddCandidates,
}: CandidateSearchDialogProps) {
  // Filter state
  const [location, setLocation] = useState("");
  const [salaryRange, setSalaryRange] = useState<[number, number]>([0, 500000]);
  const [availability, setAvailability] = useState<string[]>([]);
  const [minExperience, setMinExperience] = useState(0);
  
  // Results state
  const [candidates, setCandidates] = useState<SearchCandidate[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  
  // UI state
  const [isSearching, setIsSearching] = useState(false);
  const [isScoring, setIsScoring] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showOnlyHighScores, setShowOnlyHighScores] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Job skills for reference
  const [jobSkills, setJobSkills] = useState<Skill[]>([]);

  useEffect(() => {
    if (open && jobId) {
      fetchJobSkills();
    }
  }, [open, jobId]);

  const fetchJobSkills = async () => {
    const { data } = await supabase
      .from("job_skills")
      .select("skill_id, skills(id, name)")
      .eq("job_id", jobId);
    
    if (data) {
      setJobSkills(data.map((js: any) => js.skills).filter(Boolean));
    }
  };

  const handleSearch = async (searchPage = 1) => {
    setIsSearching(true);
    setHasSearched(true);
    
    try {
      const { data, error } = await supabase.functions.invoke("search-candidates-for-job", {
        body: {
          jobId,
          filters: {
            location: location || undefined,
            salaryMin: salaryRange[0] > 0 ? salaryRange[0] : undefined,
            salaryMax: salaryRange[1] < 500000 ? salaryRange[1] : undefined,
            availability: availability.length > 0 ? availability : undefined,
            minExperience: minExperience > 0 ? minExperience : undefined,
          },
          page: searchPage,
          limit: 50,
        },
      });

      if (error) throw error;

      setCandidates(data.candidates);
      setTotal(data.total);
      setPage(searchPage);
      setHasMore(data.hasMore);
      setSelectedIds(new Set());

      toast({
        title: "Search complete",
        description: `Found ${data.total} candidates`,
      });
    } catch (error: any) {
      console.error("Search error:", error);
      toast({
        title: "Search failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleAIScore = async () => {
    if (selectedIds.size === 0) {
      toast({
        title: "No candidates selected",
        description: "Select up to 20 candidates to score with AI",
        variant: "destructive",
      });
      return;
    }

    if (selectedIds.size > 20) {
      toast({
        title: "Too many selected",
        description: "Please select a maximum of 20 candidates for AI scoring",
        variant: "destructive",
      });
      return;
    }

    setIsScoring(true);

    try {
      const { data, error } = await supabase.functions.invoke("batch-score-candidates", {
        body: {
          jobId,
          candidateIds: Array.from(selectedIds),
        },
      });

      if (error) throw error;

      // Update candidates with new AI scores
      const scoreMap = new Map<string, number>(data.scores.map((s: any) => [s.candidateId, s.overallScore]));
      
      setCandidates(prev => prev.map(c => ({
        ...c,
        aiScore: scoreMap.has(c.id) ? scoreMap.get(c.id)! : c.aiScore,
        aiScoredAt: scoreMap.has(c.id) ? new Date().toISOString() : c.aiScoredAt,
      })));

      toast({
        title: "AI Scoring complete",
        description: `Scored ${data.newlyScored} candidates (${data.cached} from cache)`,
      });
    } catch (error: any) {
      console.error("AI scoring error:", error);
      toast({
        title: "AI Scoring failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsScoring(false);
    }
  };

  const handleAddToKanban = (candidateIds: string[]) => {
    onAddCandidates(candidateIds);
    toast({
      title: "Candidates added",
      description: `${candidateIds.length} candidate(s) added to Kanban`,
    });
  };

  const clearFilters = () => {
    setLocation("");
    setSalaryRange([0, 500000]);
    setAvailability([]);
    setMinExperience(0);
  };

  const hasActiveFilters = location || salaryRange[0] > 0 || salaryRange[1] < 500000 || 
    availability.length > 0 || minExperience > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Find Candidates for: {jobTitle}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Search controls */}
          <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
            {/* Job skills reference */}
            {jobSkills.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Required Skills</Label>
                <div className="flex flex-wrap gap-1">
                  {jobSkills.map(skill => (
                    <Badge key={skill.id} variant="secondary" className="text-xs">
                      {skill.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Main search bar */}
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  placeholder="Filter by location (e.g., Mumbai, Bangalore)"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={hasActiveFilters ? "border-primary" : ""}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-2 h-5 w-5 p-0 flex items-center justify-center">
                    !
                  </Badge>
                )}
              </Button>
              <Button onClick={() => handleSearch(1)} disabled={isSearching}>
                {isSearching ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Search
              </Button>
            </div>

            {/* Advanced filters */}
            {showFilters && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label className="text-sm">Salary Range</Label>
                  <div className="px-2">
                    <Slider
                      value={salaryRange}
                      onValueChange={(value) => setSalaryRange(value as [number, number])}
                      min={0}
                      max={500000}
                      step={10000}
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>${(salaryRange[0] / 1000).toFixed(0)}k</span>
                      <span>${(salaryRange[1] / 1000).toFixed(0)}k</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Min Experience (years)</Label>
                  <Select
                    value={minExperience.toString()}
                    onValueChange={(v) => setMinExperience(parseInt(v))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Any</SelectItem>
                      <SelectItem value="1">1+ years</SelectItem>
                      <SelectItem value="3">3+ years</SelectItem>
                      <SelectItem value="5">5+ years</SelectItem>
                      <SelectItem value="10">10+ years</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Availability</Label>
                  <Select
                    value={availability[0] || "all"}
                    onValueChange={(v) => setAvailability(v === "all" ? [] : [v])}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any status</SelectItem>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="open">Open to offers</SelectItem>
                      <SelectItem value="employed">Employed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="w-4 h-4 mr-1" />
                    Clear filters
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Results section */}
          {hasSearched && (
            <div className="space-y-4">
              {/* Action bar */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="high-scores"
                      checked={showOnlyHighScores}
                      onCheckedChange={setShowOnlyHighScores}
                    />
                    <Label htmlFor="high-scores" className="text-sm">
                      Show only ≥70% matches
                    </Label>
                  </div>
                </div>

                <Button
                  onClick={handleAIScore}
                  disabled={isScoring || selectedIds.size === 0}
                  variant="secondary"
                >
                  {isScoring ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Brain className="w-4 h-4 mr-2" />
                  )}
                  AI Score Selected ({selectedIds.size}/20)
                </Button>
              </div>

              {/* Results table */}
              <CandidateSearchResults
                candidates={candidates}
                total={total}
                page={page}
                limit={50}
                hasMore={hasMore}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                onPageChange={(p) => handleSearch(p)}
                onAddToKanban={handleAddToKanban}
                isScoring={isScoring}
                showOnlyHighScores={showOnlyHighScores}
              />
            </div>
          )}

          {/* Empty state */}
          {!hasSearched && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Sparkles className="w-12 h-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium">Find matching candidates</h3>
              <p className="text-sm text-muted-foreground max-w-md mt-2">
                Search across all candidates to find the best matches for this job. 
                Use filters to narrow down results, then select candidates for AI-powered scoring.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
